import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getFinancedDealTranchesByDeal } from '../../services/financedDealTranchesService';
import {
  getInterestPaymentsForDeal,
  computeExpectedInterestForPeriod,
  getSchedulePeriodsForDeal,
  normalizeInterestPeriodAnchorDay,
  isDealActiveInPeriod,
} from '../../services/interestPaymentsService';
import { FileText, ChevronDown, ChevronRight } from 'lucide-react';

function formatAmount(n) {
  if (n == null || typeof n !== 'number' || Number.isNaN(n)) return '—';
  const s = String(Math.max(0, Math.floor(n)));
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' $';
}

function formatDecimal(n) {
  if (n == null || typeof n !== 'number' || Number.isNaN(n)) return '—';
  const fixed = Math.max(0, n).toFixed(2);
  const [intPart, dec] = fixed.split('.');
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ',' + dec + ' $';
}

function formatDateWithLocale(str, locale) {
  if (!str || typeof str !== 'string') return '—';
  const d = str.slice(0, 10);
  if (!d) return '—';
  try {
    const dateLocale = locale === 'fr' ? 'fr-CA' : 'en-CA';
    return new Date(d + 'T12:00:00').toLocaleDateString(dateLocale, { month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}

export default function InterestReceivedReport({
  financedDeals = [],
  properties = [],
  onBack,
  interestPeriodAnchorDay = 1,
}) {
  const { t, language } = useLanguage();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDeals, setExpandedDeals] = useState(() => new Set());
  const loadIdRef = useRef(0);

  const toggleDeal = (dealId) => {
    setExpandedDeals((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  };

  const activeDeals = useMemo(
    () => (financedDeals || []).filter((d) => d.status !== 'cancelled' && d.fundsDisbursedAt),
    [financedDeals]
  );
  const dealIdsKey = useMemo(
    () => activeDeals.map((d) => d.id).sort().join(','),
    [activeDeals]
  );

  useEffect(() => {
    if (!activeDeals.length) {
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }
    const loadId = ++loadIdRef.current;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const year = selectedYear;
    const dealsToLoad = activeDeals;
    const propsList = properties || [];
    (async () => {
      try {
        const allRows = [];
        for (const deal of dealsToLoad) {
          if (cancelled) break;
          let tranches;
          let paymentsForDeal;
          try {
            tranches = await getFinancedDealTranchesByDeal(deal.id);
          } catch (e) {
            if (e?.code === 'permission-denied' || (e?.message && e.message.includes('permission'))) {
              throw new Error('TRANCHES_PERMISSION');
            }
            throw e;
          }
          try {
            paymentsForDeal = await getInterestPaymentsForDeal(deal.id);
          } catch (e) {
            if (e?.code === 'permission-denied' || (e?.message && e.message.includes('permission'))) {
              throw new Error('PAYMENTS_PERMISSION');
            }
            throw e;
          }
          const paymentByPeriod = {};
          for (const p of paymentsForDeal || []) {
            let key = p.periodKey;
            if (!key && p.periodStart) {
              const ps = p.periodStart;
              key = typeof ps === 'string' ? ps.slice(0, 7) : (ps?.toDate?.()?.toISOString?.()?.slice(0, 7) || '');
            }
            if (key) paymentByPeriod[key] = p;
          }
          const prop = propsList.find((p) => p.id === deal.propertyId);
          const address = prop
            ? [prop.address, prop.city, prop.province, prop.postalCode].filter(Boolean).join(', ') || '—'
            : '—';
          const anchor = normalizeInterestPeriodAnchorDay(interestPeriodAnchorDay);
          const yearStart = `${year}-01-01`;
          const yearEnd = `${year}-12-31`;
          const periods = getSchedulePeriodsForDeal(deal, { interestPeriodAnchorDay: anchor }).filter(
            (p) => p.periodEnd >= yearStart && p.periodStart <= yearEnd
          );
          for (const { periodKey, periodStart, periodEnd } of periods) {
            if (!isDealActiveInPeriod(deal, periodStart, periodEnd)) continue;
            const expected = computeExpectedInterestForPeriod(deal, tranches, periodStart, periodEnd);
            const payment = paymentByPeriod[periodKey] || null;
            const received = payment?.paidAmount != null ? Number(payment.paidAmount) : null;
            const status = payment?.status || (received != null && received > 0 ? 'paid' : 'pending');
            const periodLabel =
              anchor === 1
                ? formatDateWithLocale(`${periodKey}-01`, language)
                : `${formatDateWithLocale(periodStart, language)} → ${formatDateWithLocale(periodEnd, language)}`;
            allRows.push({
              dealId: deal.id,
              propertyAddress: address,
              principalAmount: deal.principalAmount ?? 0,
              periodKey,
              periodLabel,
              expected,
              received: received ?? 0,
              status,
              discrepancyNote: payment?.discrepancyNote || null,
            });
          }
        }
        if (!cancelled && loadId === loadIdRef.current) {
          setRows(allRows);
          setError(null);
        }
      } catch (err) {
        if (!cancelled && loadId === loadIdRef.current) {
          setRows([]);
          const msg = err?.message || '';
          const isPermission = err?.code === 'permission-denied' || msg.includes('permission') || msg === 'TRANCHES_PERMISSION' || msg === 'PAYMENTS_PERMISSION';
          let displayMsg = t('interestReport.errorPermission');
          if (msg === 'TRANCHES_PERMISSION') displayMsg = t('interestReport.errorPermissionTranches');
          else if (msg === 'PAYMENTS_PERMISSION') displayMsg = t('interestReport.errorPermissionPayments');
          else if (isPermission) displayMsg = t('interestReport.errorPermission');
          else displayMsg = err?.message || t('interestReport.errorLoad');
          setError(displayMsg);
        }
      } finally {
        if (!cancelled && loadId === loadIdRef.current) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dealIdsKey, selectedYear, language, interestPeriodAnchorDay]);

  const formatDate = (str) => formatDateWithLocale(str, language);
  const years = [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2];

  const rowsByProperty = useMemo(() => {
    const byDeal = {};
    for (const r of rows) {
      const key = r.dealId;
      if (!byDeal[key]) byDeal[key] = { propertyAddress: r.propertyAddress, principalAmount: r.principalAmount, months: [] };
      byDeal[key].months.push(r);
    }
    return Object.entries(byDeal).map(([dealId, data]) => ({ dealId, ...data }));
  }, [rows]);

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="text-gray-600 hover:text-gray-800 text-sm font-medium"
      >
        ← {t('interestReport.back')}
      </button>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-600" />
          {t('interestReport.title')}
        </h2>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">{t('interestReport.year')}</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-800 text-sm mb-4">
          {error}
        </div>
      )}
      {loading ? (
        <p className="text-gray-500 py-4">{t('interestReport.loading')}</p>
      ) : rows.length === 0 && !error ? (
        <p className="text-gray-500 py-4">{t('interestReport.empty')}</p>
      ) : rows.length > 0 ? (
        <div className="space-y-2">
          {rowsByProperty.map(({ dealId, propertyAddress, principalAmount, months }) => {
            const isExpanded = expandedDeals.has(dealId);
            return (
              <div key={dealId} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleDeal(dealId)}
                  className={`w-full bg-gray-50 px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-left hover:bg-gray-100 transition-colors ${isExpanded ? 'border-b border-gray-200' : ''}`}
                >
                  <span className="flex items-center gap-2 font-medium text-gray-900" title={propertyAddress}>
                    {isExpanded ? <ChevronDown className="w-4 h-4 shrink-0 text-gray-500" /> : <ChevronRight className="w-4 h-4 shrink-0 text-gray-500" />}
                    {propertyAddress}
                  </span>
                  <span className="text-sm text-gray-600">{t('interestReport.colCapital')}: {formatAmount(principalAmount)}</span>
                </button>
                {isExpanded && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50/70">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('interestReport.colMonth')}</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('interestReport.colExpected')}</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('interestReport.colReceived')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('interestReport.colStatus')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('interestReport.colNote')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {months.map((r) => (
                      <tr
                        key={r.periodKey}
                        className={
                          r.status === 'pending' || r.status === 'partial'
                            ? 'bg-amber-50/50'
                            : r.discrepancyNote
                              ? 'bg-amber-50/30'
                              : ''
                        }
                      >
                        <td className="px-3 py-2 text-gray-700">{r.periodLabel}</td>
                        <td className="px-3 py-2 text-right text-gray-700">{formatDecimal(r.expected)}</td>
                        <td className="px-3 py-2 text-right font-medium">{formatDecimal(r.received)}</td>
                        <td className="px-3 py-2">
                          <span
                            className={
                              r.status === 'paid' || r.status === 'overpaid'
                                ? 'text-green-700'
                                : r.status === 'partial'
                                  ? 'text-amber-700'
                                  : 'text-amber-600 font-medium'
                            }
                          >
                            {t(`interestReport.status_${r.status}`)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-500 max-w-[180px] truncate" title={r.discrepancyNote || ''}>{r.discrepancyNote || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
